import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, X, NotebookPen } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { SessionRow } from "@/components/SessionCard";
import { PendingList } from "@/components/Offline";

export default function Journal() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["sessions", debounced],
    queryFn: () => api.get(`/sessions?limit=200${debounced ? `&q=${encodeURIComponent(debounced)}` : ""}`).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
  const { data: tags = [] } = useQuery({ queryKey: ["tags"], queryFn: () => api.get("/tags").then((r) => r.data) });

  return (
    <div>
      <PageHeader eyebrow={`${sessions.length} session${sessions.length === 1 ? "" : "s"}${debounced ? " found" : ""}`} title="Journal" />

      <div className="px-5">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input data-testid="journal-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes, techniques…" className="field pl-11 pr-10" />
          {q && (
            <button data-testid="journal-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#71717A] hover:text-white">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {!debounced && (
        <div className="px-5 mt-6 space-y-6">
          <PendingList />
        </div>
      )}

      {tags.length > 0 && !debounced && (
        <section className="mt-6">
          <div className="eyebrow px-5 mb-2.5">Your positions & techniques</div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 pb-1" data-testid="journal-tag-strip">
            {tags.map((t) => (
              <Link key={t.name} to={`/tag/${encodeURIComponent(t.name)}`} data-testid={`journal-tag-${t.name.replace(/\s+/g, "-")}`} className="chip shrink-0">
                {t.name} <span className="text-[#71717A] text-xs">{t.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="px-5 mt-6 space-y-2.5" data-testid="journal-list">
        {sessions.map((s, i) => <SessionRow key={s.id} session={s} index={i} />)}
        {!isLoading && sessions.length === 0 && (
          <div className="card p-8 text-center" data-testid="journal-empty">
            <NotebookPen className="mx-auto text-[#52525B] mb-3" size={28} />
            <div className="font-semibold">{debounced ? "Nothing matches that" : "No sessions yet"}</div>
            <p className="text-sm text-[#A1A1AA] mt-1">{debounced ? "Try a different word." : "Your organised notes will live here."}</p>
            {!debounced && <Link to="/" className="btn-primary mt-5" data-testid="journal-log-first">Log a session</Link>}
          </div>
        )}
      </section>
    </div>
  );
}
